import React from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import CTAButton from '@/components/CTAButton';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Zap, Star, Gift } from 'lucide-react';
const GeniusCardUpsellHero = () => {
  const {
    isMobile
  } = useBreakpoint();
  return <SectionLayout backgroundColor="bg-gradient-to-br from-white via-[#FEE6D8]/10 to-white" paddingY="py-12 md:py-16 lg:py-24" containerWidth="wide" enableMagicBackground={true} patternColor="#003a70" backgroundPattern="soft-mesh" patternOpacity={3} decorativeElements={<>
          <div className="absolute top-1/4 left-1/6 w-64 h-64 md:w-80 md:h-80 bg-[#FEE6D8]/20 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-1/3 right-1/6 w-72 h-72 md:w-96 md:h-96 bg-[#003a70]/5 rounded-full blur-3xl animate-float" style={{
      animationDelay: '2s'
    }}></div>
          <div className="absolute top-2/3 left-1/2 w-48 h-48 md:w-64 md:h-64 bg-gradient-to-r from-[#FEE6D8]/15 to-[#003a70]/5 rounded-full blur-2xl animate-float" style={{
      animationDelay: '4s'
    }}></div>
        </>}>
      <div className="text-center space-y-8 md:space-y-12">
        
        {/* Header/Hook */}
        <div className="space-y-4 animate-fade-in">
          <div className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/80 rounded-full shadow-lg border-2 border-[#FEE6D8]/50 hover:shadow-xl transition-all duration-300 hover:scale-105">
            <Gift className="text-[#003a70]" size={isMobile ? 20 : 24} />
            <span className="text-[#003a70] font-semibold text-sm md:text-3xl">Special One Time Upgrade Offer</span>
          </div>
          
          <h1 className={cn("font-bold text-[#003a70] leading-tight", isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl")}>
            Wait! Don't Leave Empty-Handed...
          </h1>
        </div>

        {/* Main Headline */}
        <div className="space-y-4 animate-fade-in" style={{
        animationDelay: '0.2s'
      }}>
          <h2 className={cn("font-bold text-[#003a70] leading-tight", isMobile ? "text-3xl md:text-4xl" : "text-4xl lg:text-5xl xl:text-6xl")}>
            Get the Complete
            <br />
            <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
              "Done For You"
            </span>
            <br />
            Asset Pack
          </h2>
          
          <div className="flex justify-center items-center gap-2 md:gap-3">
            {[...Array(5)].map((_, i) => <Star key={i} className="text-[#FEE6D8] fill-[#FEE6D8] animate-pulse" style={{
            animationDelay: `${i * 0.1}s`
          }} size={isMobile ? 20 : 24} />)}
            <span className="text-[#003a70] font-semibold ml-2">Everything Ready to Use</span>
          </div>
        </div>

        {/* Subheadline */}
        <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" style={{
        animationDelay: '0.4s'
      }}>
          <p className={cn("text-[#003a70]/80 leading-relaxed font-medium", isMobile ? "text-lg" : "text-xl lg:text-2xl")}>
            Why spend hours creating templates, scripts, and designs when you can have 
            <span className="font-bold text-[#003a70]"> everything ready to customize and deploy immediately?</span>
          </p>
          
          <div className="bg-gradient-to-r from-[#FEE6D8]/30 to-[#FEE6D8]/20 rounded-2xl p-6 md:p-8 border border-[#FEE6D8]/40 shadow-lg backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#003a70]/10 rounded-full flex items-center justify-center animate-pulse">
                <Zap className="text-[#003a70]" size={24} />
              </div>
            </div>
            <p className={cn("text-[#003a70] font-semibold leading-relaxed", isMobile ? "text-lg" : "text-xl")}>
              Skip the setup. Skip the guesswork. Get straight to <span className="text-[#003a70] font-bold">results</span>.
            </p>
          </div>
        </div>

        {/* Video Placeholder */}
        <div className="max-w-4xl mx-auto animate-fade-in" style={{
        animationDelay: '0.6s'
      }}>
          <AspectRatio ratio={16 / 9} className="rounded-xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#003a70]/5 to-[#FEE6D8]/20 hover:shadow-3xl transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-center h-full bg-gradient-to-br from-[#003a70]/10 via-[#FEE6D8]/20 to-[#003a70]/5 border-2 border-dashed border-[#003a70]/30 hover:border-[#003a70]/50 transition-all duration-300">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto bg-[#003a70]/15 rounded-full flex items-center justify-center hover:bg-[#003a70]/25 transition-all duration-300 cursor-pointer group">
                  <div className="w-0 h-0 border-l-[12px] md:border-l-[16px] border-l-[#003a70] border-y-[8px] md:border-y-[12px] border-y-transparent ml-1 group-hover:scale-110 transition-transform duration-300"></div>
                </div>
                <p className="text-[#003a70] font-medium text-sm md:text-base">
                  Video: See Everything You Get in the Asset Pack
                </p>
              </div>
            </div>
          </AspectRatio>
        </div>

        {/* CTA Button */}
        <div className="pt-4 space-y-4 animate-fade-in" style={{
        animationDelay: '0.8s'
      }}>
          <CTAButton variant="primary" className={cn("font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300", isMobile ? "text-lg px-8 py-4" : "text-xl px-12 py-5")}>
            Yes! Give Me the Complete Asset Pack
          </CTAButton>
          
          <p className="text-[#003a70]/60 text-sm md:text-base">
            One-time offer • Everything you need to succeed
          </p>
        </div>

      </div>
    </SectionLayout>;
};
export default GeniusCardUpsellHero;